import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import _ from "lodash";
import * as actions from "../Common/actions";
import { toastError, toastSuccess, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession, copyToClipboard } from "../../utils";
import Pagination from "@mui/lab/Pagination";
import TextField from "@mui/material/TextField";
import CustomShowModal from "../Common/customShowModal";
import PayoutPasswordModal from './PayoutPasswordModal';
import SearchInput from "../../components/SearchInput";
import ActionButton from "../../components/ActionButton";
import EmptyState from "../../components/EmptyState";
import { Wallet, User, Mail, Phone, DollarSign, Calendar, Clock, Copy, ClipboardCheck, CreditCard, Receipt, Send, ArrowRightLeft, Download } from 'lucide-react';

const getTimeAgo = (dateString) => {
  if (!dateString) return "-";
  const updatedDate = new Date(dateString);
  const now = new Date();
  let years = now.getFullYear() - updatedDate.getFullYear();
  let months = now.getMonth() - updatedDate.getMonth();
  let days = now.getDate() - updatedDate.getDate();
  if (days < 0) { months--; const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0); days += lastMonth.getDate(); }
  if (months < 0) { years--; months += 12; }
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0) parts.push(`${days}d`);
  if (parts.length === 0) return "Today";
  return parts.join(' ');
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

function Payout(props) {
  const [payouts, setPayouts] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    user_id: "", request_id: "", userEmail: "",
    utr: "", ref: "", gross_amt: "", tds: "", amt: "",
  });
  const [withdrawOld, setWithdrawOld] = useState([]);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState({ limit: 50, offset: 0, page: 1, search: "" });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [exportLoading, setExportLoading] = useState('');

  useEffect(() => {
    const sessionValid = checkUserDetailsSession();
    if (sessionValid) {
      setIsAuthenticated(true);
      getAllPayouts();
      onGetWithdrawOld(filters);
    } else {
      setShowPasswordModal(true);
      clearUserDetailsSession();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const intervalId = setInterval(() => {
      const sessionValid = checkUserDetailsSession();
      if (!sessionValid) {
        setIsAuthenticated(false);
        setShowPasswordModal(true);
        setPasswordError('Your session has expired. Please re-enter the password.');
      }
    }, 300000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const sessionValid = checkUserDetailsSession();
        if (!sessionValid) {
          setIsAuthenticated(false);
          setShowPasswordModal(true);
          setPasswordError('Your session has expired. Please re-enter the password.');
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const getAllPayouts = () => {
    props.setLoader(true);
    actions.getWithdrawRequestAdmin().then((res) => {
      props.setLoader(false);
      setPayouts(res.body);
    }).catch((err) => {
      props.setLoader(false);
      toastError(err?.message || 'Failed to load payouts');
    });
  };

  const onClickSettleAndEmail = () => {
    if (!selectedItem.utr || !selectedItem.ref || !selectedItem.gross_amt || !selectedItem.tds || !selectedItem.amt) {
      toastError("All fields are required");
      return;
    }
    onConfirmSettle();
  };

  const onClickSettle = (item) => {
    setSelectedItem({
      user_id: item.user_id, request_id: item.request_id, userEmail: item.email,
      utr: "", ref: "", gross_amt: "", tds: "", amt: "",
    });
    setConfirmOpen(true);
  };

  const onConfirmSettle = () => {
    props.setLoader(true);
    actions.sentMoneyByAdmin(selectedItem).then((res) => {
      setConfirmOpen(false);
      getAllPayouts();
      onGetWithdrawOld(filters);
      toastSuccess(res.message);
      props.setLoader(false);
    }).catch((err) => {
      setConfirmOpen(false);
      props.setLoader(false);
      toastError(err?.message || 'Failed to settle payout');
    });
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetWithdrawOld(newFilters);
  };

  const onGetWithdrawOld = (filters) => {
    props.setLoader(true);
    actions.getWithdrawHistoryAdmin({ ...filters }).then((res) => {
      props.setLoader(false);
      setWithdrawOld(res.body);
      setCount(res.total);
    }).catch((err) => {
      props.setLoader(false);
      toastError(err?.message || 'Failed to load history');
    });
  };

  const delaySearch = useCallback(
    _.debounce((filters) => onGetWithdrawOld(filters), 500),
    []
  );

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const handlePasswordVerification = (password) => {
    setPasswordLoading(true);
    setPasswordError('');
    actions.verifyUserDetailsPassword({ password }).then((res) => {
      setPasswordLoading(false);
      setShowPasswordModal(false);
      setIsAuthenticated(true);
      setUserDetailsSession(Date.now());
      getAllPayouts();
      onGetWithdrawOld(filters);
      toastSuccess('Access granted');
    }).catch((err) => {
      setPasswordLoading(false);
      setPasswordError(err?.message || 'Invalid password. Please try again.');
    });
  };

  const onExportPayout = (period) => {
    setExportLoading(period);
    actions.exportPayoutHistoryAdmin(period).then(() => {
      setExportLoading('');
      toastSuccess('Export downloaded');
    }).catch((err) => {
      setExportLoading('');
      toastError(err?.message || 'Export failed');
    });
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '2px',
      backgroundColor: 'var(--bg-elevated)',
      '& fieldset': { borderColor: 'var(--border)' },
      '&:hover fieldset': { borderColor: 'var(--primary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
    },
    '& .MuiInputLabel-root': {
      color: 'var(--text-secondary)',
      '&.Mui-focused': { color: 'var(--primary)' },
    },
  };

  const renderPayoutCard = (item, index) => (
    <Paper
      key={item.request_id || index}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <Wallet size={20} color="#64748B" />
          <span>Payout</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.name || 'Unknown User'}
            </Typography>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 'auto', minWidth: 420, justifyContent: 'flex-end' }}>
            {item.revenue && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#10B981', fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                <DollarSign size={16} style={{ color: '#10B981' }} />{item.revenue}
              </span>
            )}
            <span style={{ color: 'var(--border)', margin: '0 2px' }}>|</span>
            <Calendar size={14} style={{ color: '#F59E0B' }} />
            <span style={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: 500 }}>Bank Updated:</span>
            <span style={{ color: '#F59E0B', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace' }}>
              {formatDate(item.bank_updated_on)}
            </span>
            <Chip
              icon={<Clock size={11} />}
              label={getTimeAgo(item.bank_updated_on)}
              size="small"
              sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#EF4444',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 22,
                ml: 0.5,
                '& .MuiChip-icon': { color: '#EF4444' },
              }}
            />
          </span>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-PO-{item.request_id}</span>
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          <CopyMeta icon={<Phone size={13} />} label="Phone" value={item.phone} />
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <ActionButton variant="settle" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', gap: 0.5 }} onClick={() => onClickSettle(item)}>
              <ArrowRightLeft size={13} /> Settle
            </ActionButton>
          </div>
        </div>
      </div>
    </Paper>
  );

  const renderHistoryCard = (item, index) => (
    <Paper
      key={item.request_id || index}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <Receipt size={20} color="#64748B" />
          <span>Settled</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.name || 'Unknown User'}
            </Typography>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 'auto', minWidth: 420, justifyContent: 'flex-end' }}>
            <Calendar size={14} style={{ color: '#F59E0B' }} />
            <span style={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: 500 }}>Settled:</span>
            <span style={{ color: '#F59E0B', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace' }}>
              {item.settled_on || '-'}
            </span>
          </span>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-U-{item.user_id}</span>
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          <CopyMeta icon={<CreditCard size={13} />} label="UTR" value={item.utr} />
          <CopyMeta icon={<Receipt size={13} />} label="REF" value={item.ref} />
          {item.email_amt && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
              <span style={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: 500 }}>Net ₹:</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#10B981', fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                {item.email_amt}
              </span>
            </span>
          )}
        </div>
        <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
          <span className="approved-release-card__meta-item" style={{ color: '#94A3B8' }}>
            Amount: <strong style={{ color: '#F1F5F9' }}>{item.amount || '-'}</strong>
          </span>
          <span className="approved-release-card__meta-item" style={{ color: '#94A3B8' }}>
            Gross: <strong style={{ color: '#F1F5F9' }}>{item.gross_amt || '-'}</strong>
          </span>
          <span className="approved-release-card__meta-item" style={{ color: '#94A3B8' }}>
            TDS: <strong style={{ color: '#EF4444' }}>{item.tds || '-'}</strong>
          </span>
        </div>
      </div>
    </Paper>
  );

  if (!isAuthenticated) {
    return (
      <PayoutPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
    );
  }

  return (
    <>
      <PayoutPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
      <Grid container>
        {/* ========== Review Required Section ========== */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
              Review Required
            </Typography>
            <Chip
              icon={<Wallet size={14} />}
              label={`${payouts.length} Pending`}
              sx={{
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                color: '#F59E0B',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#F59E0B' },
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box className="approved-release-list" sx={{ mb: 3 }}>
            {payouts.length ? (
              payouts.map((item, index) => renderPayoutCard(item, index))
            ) : (
              <Paper sx={{ p: 0 }}>
                <table style={{ width: '100%' }}><tbody>
                  <EmptyState message="No Pending Requests" colSpan={1} />
                </tbody></table>
              </Paper>
            )}
          </Box>
        </Grid>

        {/* ========== Divider ========== */}
        <Grid item xs={12}>
          <Divider sx={{ borderColor: 'var(--border)', my: 1 }} />
        </Grid>

        {/* ========== History Section ========== */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} mt={2}>
            <Box sx={{ maxWidth: 420, flex: 1 }}>
              <SearchInput
                value={filters.search}
                onChange={onChangeSearch}
                placeholder="Search payout history..."
              />
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} flexShrink={0}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
                  History
                </Typography>
                <Chip
                  icon={<Receipt size={14} />}
                  label={`${count} Total`}
                  sx={{
                    backgroundColor: 'rgba(108, 99, 255, 0.12)',
                    color: '#6C63FF',
                    border: '1px solid rgba(108, 99, 255, 0.25)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    height: 34,
                    flexShrink: 0,
                    '& .MuiChip-icon': { color: '#6C63FF' },
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={0.8}>
                <span style={{ color: '#94A3B8', fontSize: '0.78rem', fontWeight: 500 }}>Export :</span>
                <ActionButton variant="settle" sx={{ py: 0.3, px: 1.2, fontSize: '0.73rem', gap: 0.5 }}
                  disabled={!!exportLoading} onClick={() => onExportPayout('current_month')}>
                  <Download size={13} /> {exportLoading === 'current_month' ? 'Exporting...' : 'This Month'}
                </ActionButton>
                <ActionButton variant="settle" sx={{ py: 0.3, px: 1.2, fontSize: '0.73rem', gap: 0.5 }}
                  disabled={!!exportLoading} onClick={() => onExportPayout('1_month')}>
                  <Download size={13} /> {exportLoading === '1_month' ? 'Exporting...' : '1 Month'}
                </ActionButton>
                <ActionButton variant="settle" sx={{ py: 0.3, px: 1.2, fontSize: '0.73rem', gap: 0.5 }}
                  disabled={!!exportLoading} onClick={() => onExportPayout('3_months')}>
                  <Download size={13} /> {exportLoading === '3_months' ? 'Exporting...' : '3 Months'}
                </ActionButton>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box className="approved-release-list">
            {withdrawOld.length ? (
              withdrawOld.map((item, index) => renderHistoryCard(item, index))
            ) : (
              <Paper sx={{ p: 0 }}>
                <table style={{ width: '100%' }}><tbody>
                  <EmptyState message="No Payout History Found" colSpan={1} />
                </tbody></table>
              </Paper>
            )}
          </Box>
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                showFirstButton
                showLastButton
                onChange={onPageChange}
                page={filters.page || 1}
              />
            </Box>
          )}
        </Grid>

        {/* ========== Settlement Modal ========== */}
        <CustomShowModal
          handleClose={() => setConfirmOpen(false)}
          title="Settle Payout"
          open={confirmOpen}
          maxWidth="sm"
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="UTR Number" variant="outlined" size="small" value={selectedItem.utr}
              onChange={(e) => setSelectedItem((prev) => ({ ...prev, utr: e.target.value }))} fullWidth sx={inputSx} />
            <TextField label="Reference Number" variant="outlined" size="small" value={selectedItem.ref}
              onChange={(e) => setSelectedItem((prev) => ({ ...prev, ref: e.target.value }))} fullWidth sx={inputSx} />
            <TextField label="Gross Amount (INR)" variant="outlined" size="small" value={selectedItem.gross_amt}
              onChange={(e) => setSelectedItem((prev) => ({ ...prev, gross_amt: e.target.value }))} fullWidth type="number" sx={inputSx} />
            <TextField label="TDS Amount" variant="outlined" size="small" value={selectedItem.tds}
              onChange={(e) => setSelectedItem((prev) => ({ ...prev, tds: e.target.value }))} fullWidth type="number" sx={inputSx} />
            <TextField label="Net Payable Amount" variant="outlined" size="small" value={selectedItem.amt}
              onChange={(e) => setSelectedItem((prev) => ({ ...prev, amt: e.target.value }))} fullWidth type="number" sx={inputSx} />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1} mt={2.5} py={1}>
            <ActionButton variant="info" onClick={() => setConfirmOpen(false)}>
              Cancel
            </ActionButton>
            <ActionButton variant="settle" sx={{ gap: 0.5 }} onClick={onConfirmSettle}>
              <ArrowRightLeft size={14} /> Just Settle
            </ActionButton>
            <ActionButton variant="success" sx={{ gap: 0.5 }} onClick={onClickSettleAndEmail}>
              <Send size={14} /> Settle & Email
            </ActionButton>
          </Box>
        </CustomShowModal>
      </Grid>
    </>
  );
}

export default Payout;
